import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { MessageCircle, Menu, X } from "lucide-react";
import { BRAND, WHATSAPP_LINK } from "@/lib/constants";

const LINKS = [
  { href: "#how", label: "كيف يعمل" },
  { href: "#benefits", label: "المميزات" },
  { href: "#pricing", label: "العروض" },
  { href: "#proofs", label: "الإثباتات" },
  { href: "#testimonials", label: "آراء العملاء" },
  { href: "#contact", label: "تواصل معنا" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-black/5 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a href="#" className="flex items-center gap-2.5" data-testid="navbar-logo">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-green to-brand-green-light flex items-center justify-center shadow-md">
              <span className="text-brand-gold font-heading font-extrabold text-xl">M</span>
            </div>
            <div className="leading-tight">
              <div className="font-heading font-extrabold text-brand-green text-lg">{BRAND.name}</div>
              <div className="text-[11px] text-brand-gold tracking-wide font-semibold">{BRAND.tagline}</div>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-7">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.href.replace('#','')}`}
                className="text-sm font-semibold text-brand-ink/80 hover:text-brand-green transition-colors relative group"
              >
                {l.label}
                <span className="absolute -bottom-1 inset-x-0 h-0.5 bg-brand-gold scale-x-0 group-hover:scale-x-100 transition-transform origin-center" />
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <Button
              asChild
              data-testid="navbar-whatsapp-btn"
              className="bg-[#25D366] hover:bg-[#1fb858] text-white rounded-full px-6 h-11 font-extrabold gap-2 shadow-md"
            >
              <a href={WHATSAPP_LINK()} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-4 h-4" />
                اطلب عبر واتساب
              </a>
            </Button>
          </div>

          <button
            className="lg:hidden w-10 h-10 rounded-lg flex items-center justify-center text-brand-green"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-testid="navbar-mobile-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden pb-4 fade-up" data-testid="navbar-mobile-menu">
            <div className="flex flex-col gap-1 p-3 bg-white rounded-2xl shadow-lg border border-black/5">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="px-4 py-3 rounded-xl text-brand-ink font-semibold hover:bg-brand-bg"
                >
                  {l.label}
                </a>
              ))}
              <div className="pt-2">
                <Button asChild className="w-full bg-[#25D366] hover:bg-[#1fb858] text-white rounded-full font-extrabold h-12">
                  <a href={WHATSAPP_LINK()} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>
                    <MessageCircle className="w-4 h-4 ms-1" /> اطلب عبر واتساب
                  </a>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
